import { useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import debounce from 'lodash/debounce';
import { t } from '@lingui/macro';
import { Trans } from '@lingui/react';
import Button from '@components/buttons';
import EmptyResults from '@components/EmptyResults';
import { Container, Flex } from '@components/layout/Grid';
import Search from '@ecosystems/events/Search';
import Spinner from '@components/Spinner';
import {
  GenericProgramSortAttribute,
  LanguageKind,
  ProgramCategory,
  useGenericProgramsQuery,
} from '@gql/generated';
import { snackbarVar } from '@lib/apollo/policies';
import { UserContext } from '@lib/contexts/UserProvider';
import ProgramListingCard from '@organisms/cards/ProgramListingCard';
import { buildProgramsFilters } from '@pages/[lang]/programs';
import ProgramsFilters from './ProgramsFilters';
import { getUserProgramAccess } from '@lib/helpers';
import { NetworkStatus } from '@apollo/client';

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 400px));
  justify-content: center;
  grid-gap: 20px;

  @media (min-width: ${({ theme }) => theme.viewports.tablet}px) {
    grid-template-columns: repeat(2, 1fr);
    grid-gap: 30px;
  }

  @media (min-width: ${({ theme }) => theme.viewports.laptop}px) {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    grid-gap: 40px;
  }
`;

const LoadMore = styled(Button)<{ category: ProgramCategory }>`
  --color: ${({ category, theme }) =>
    category === ProgramCategory['Courses']
      ? theme.colors.primary.green
      : '#242424'};
  --border-color: ${({ category, theme }) =>
    category === ProgramCategory['Courses']
      ? theme.colors.primary.green
      : '#979797'};

  color: var(--color);
  border: 1px solid var(--border-color);
  border-radius: 3px;
  padding: 12px 24px;
  background: none;

  :hover {
    color: var(--color);
    border: 1px solid var(--border-color);
    opacity: 0.8;
  }
`;

export const ProgramsSearch = ({
  ssrData,
  initialFilters,
  programCategory = ProgramCategory['Programs'],
}) => {
  const router = useRouter();
  const { query } = router;
  const lang = query?.lang as string;
  const [filters, setFilters] = useState(buildProgramsFilters(query));
  const [currentUser] = useContext(UserContext);
  const {
    data,
    loading,
    fetchMore,
    refetch,
    networkStatus,
  } = useGenericProgramsQuery({
    notifyOnNetworkStatusChange: true,
    variables: {
      page: 1,
      perPage: 12,
      ...(initialFilters || {}),
    },
    ssr: false,
  });

  const firstRender = useRef(true);
  const { current: searchRef } = useRef(
    debounce(
      ({ search, languages, instructor, sortBy, theme }) => {
        refetch({
          page: 1,
          category: programCategory,
          search: search === '' ? '*' : search,
          languages,
          instructor,
          sortBy,
          theme,
        });
      },
      500,
      {
        maxWait: 3000,
        leading: false,
        trailing: true,
      }
    )
  );

  useEffect(() => {
    refetch();
  }, [lang]);

  useEffect(() => {
    if (!firstRender.current) {
      searchRef({
        category: programCategory,
        search: filters.search,
        languages: filters.languages,
        instructor: filters.instructor,
        sortBy: filters.sortBy,
        theme: filters.theme,
      });
    } else {
      firstRender.current = false;
    }
  }, [
    filters.search,
    filters.languages,
    filters.instructor,
    filters.theme,
    filters.sortBy,
  ]);

  const loadMore = () => {
    fetchMore({
      variables: {
        page: data?.genericPrograms?.page + 1,
        perPage: 12,
        ...filters,
        category: programCategory,
        search: filters.search === '' ? '*' : filters.search,
      },
    });
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      category: programCategory,
      languages: [LanguageKind['En'], LanguageKind['Se']],
      instructor: null,
      sortBy: GenericProgramSortAttribute['NewestFirst'],
      theme: null,
      tags: null,
    });
    router.replace(
      {
        pathname: router.pathname,
        query: { lang },
      },
      undefined,
      { shallow: true }
    );
  };

  const updateFilters = (filter: keyof typeof filters, value: string) => {
    setFilters({ ...filters, [filter]: value });
    let query = {
      ...router.query,
      lang,
      [filter]: value,
    };
    if (!value) {
      const { [filter]: _val, ...otherFilters } = query;
      query = otherFilters;
    }
    router.replace(
      {
        pathname: router.pathname,
        query: query,
      },
      undefined,
      { shallow: true }
    );
    snackbarVar({
      isSnackBarOpen: true,
      snackBarMessage: t({
        id: 'programs_2021.listings_page.filter_applied_toaster',
      }),
    });
  };

  const userResolved = currentUser === null || currentUser?.id;
  const programsData = data?.genericPrograms || ssrData;

  const programs = programsData?.data;

  let content = null;
  if (!programs && (loading || !userResolved)) {
    content = [
      <Flex key="loading" justifyContent="center" alignItems="center" mt={50}>
        <Spinner size={28} spinnerColor="#242424" />
      </Flex>,
    ];
  } else if (programs?.length > 0) {
    content = [
      <Cards key="cards">
        {programs?.map(program => {
          const programAccess = getUserProgramAccess({
            user: currentUser,
            programSlug: program.slug,
            programCategory: program.category,
          });

          return (
            <ProgramListingCard
              key={program.slug}
              program={program}
              accessStatus={programAccess?.accessStatus}
              programCategory={program.category}
            />
          );
        })}
      </Cards>,
    ];

    if (programsData?.totalCount > programsData?.data?.length) {
      const loading =
        [NetworkStatus['loading'], NetworkStatus['fetchMore']].includes(
          networkStatus
        ) && !programsData?.data;

      content.push(
        <Flex key={1} justifyContent="center" mt={40}>
          <LoadMore
            category={programCategory}
            appearance="outline"
            onClick={loadMore}
            loading={loading}
          >
            <Trans id="programs_2021.listings_page.load_more_btn" />
          </LoadMore>
        </Flex>
      );
    }
  } else {
    content = [
      <EmptyResults
        key="empty"
        icon="ygb-icon-playlist-empty-ic"
        description={
          <Trans id="programs_2021.listings_page.no_programs_found" />
        }
        action={
          <Button appearance="link" onClick={clearFilters}>
            <Trans id="programs_2021.listings_page.clear_events_filters" />
          </Button>
        }
      />,
    ];
  }

  return (
    <div>
      <ProgramsFilters
        languages={filters.languages}
        instructor={filters.instructor}
        sortBy={filters.sortBy}
        theme={filters.theme}
        onInstructorChange={instructor => {
          updateFilters('instructor', instructor);
        }}
        onLanguageChange={langs => updateFilters('languages', langs)}
        onSortChange={sortBy => updateFilters('sortBy', sortBy)}
        onThemeChange={theme => updateFilters('theme', theme)}
        programCategory={programCategory}
      />
      <Container fullWidth px={20}>
        <Container flexDirection="column" mb={80}>
          <Search
            placeholder={t`programs_2021.listings_page.search_input_placeholder`}
            handleChange={value => updateFilters('search', value)}
            value={filters.search}
          />
          {content}
        </Container>
      </Container>
    </div>
  );
};
